import { useEffect, useState } from "react";
import api from "../../../services/api";
import { Trash2, Plus, Edit, UserX, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { SystemUser } from "../../../types/system-user";
import { Table } from "../../../components/table";
import { BackButton } from "../../../components/button/back-button";
import { ConfirmationModal } from "../../../components/confirmation-modal";
import { useToast } from "../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../utils";

const userSystemRolesLabel = {
  ADMIN: "Administrador Geral",
  TENANT_ADMIN: "Administrador do Ambiente",
  ATTENDANT: "Atendente",
};

type FindAllSystemUserDto = {
  data: SystemUser[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export default function ListSystemUser() {
  const { success, error } = useToast();

  const [users, setUsers] = useState<SystemUser[]>([]);
  const [userToDisable, setUserToDisable] = useState<SystemUser | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    getUsers(1);
  }, []);

  async function getUsers(pageNumber = 1) {
    try {
      const response = await api.get<FindAllSystemUserDto>("/system-user", {
        params: { page: pageNumber, limit },
      });

      setUsers(response.data.data);
      setPage(response.data.page);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  async function disableUser(id: string) {
    if (isUpdating) return;
    try {
      setIsUpdating(true);
      await api.patch(`/system-user/disabled/${id}`);
      setUserToDisable(null);
      success("Usuário do sistema atualizado com sucesso!");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsUpdating(false);
    }
  }

  const columns = [
    { key: "name", label: "Nome" },
    { key: "email", label: "E-mail" },
    {
      key: "role",
      label: "Função",
      render: (user: SystemUser) => (
        <div className="text-center flex justify-center gap-3">
          {userSystemRolesLabel[user.role]}
        </div>
      ),
    },
    { key: "appName", label: "App Name" },
    {
      key: "actions",
      label: "Ações",
      render: (user: SystemUser) => (
        <div className="text-center flex justify-center gap-3">
          <button
            onClick={() =>
              navigate(`/painel/config/system-user/editar/${user.id}`)
            }
            className="btn-link-primary cursor-pointer"
          >
            <Edit className="w-4 h-4" />
          </button>

          {user.disabled ? (
            <button
              onClick={() => setUserToDisable(user)}
              className="btn-link-success cursor-pointer"
            >
              <UserPlus className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => setUserToDisable(user)}
              className="btn-link-danger cursor-pointer"
            >
              <UserX className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="app-page">
      <BackButton />
      <div className="flex items-center justify-between mb-6">
        <h1 className="app-page-title">Usuários do Sistema</h1>
        <button
          onClick={() => navigate("/painel/config/system-user/cadastro")}
          className="btn btn-neutral cursor-pointer"
        >
          <Plus className="w-4 h-4" /> Novo Usuário
        </button>
      </div>

      <Table<SystemUser>
        columns={columns}
        data={users}
        page={page}
        total={total}
        totalPages={totalPages}
        onPageChange={(newPage) => getUsers(newPage)}
      />

      <ConfirmationModal
        isOpen={!!userToDisable}
        title={
          userToDisable && userToDisable.disabled
            ? "Confirmar ativação"
            : "Confirmar inativação"
        }
        description={
          userToDisable
            ? userToDisable.disabled
              ? `Tem certeza que deseja ativar o usuário ${userToDisable.name}?`
              : `Tem certeza que deseja desativar o usuário ${userToDisable.name}?`
            : ""
        }
        onCancel={() => setUserToDisable(null)}
        onConfirm={() => userToDisable && disableUser(userToDisable.id!)}
        isLoading={isUpdating}
      />
    </div>
  );
}
